import React, { useState, useEffect } from "react";
import { CloseIcon, ToggleIcon, getIcon } from "../GoPlanner/util/icons";
import { CloseIconWhite } from "../GoPlanner/util/icons";
import {
    clearScrollPosition,
    filterGTMDataPush,
    getFormData,
    getInitialData,
    getScrollPosition,
    setFormData,
    setScrollPosition,
    setStoreRegions,
    setStoreCities,
    getStoreRegions,
    getStoreCities,
    setStorePanels,
    getStorePanels,
} from "./util/sessions";

const App = () => {
    const pagesize = 12;

    const [panels, setPanels] = useState([]);
    const [filteredPanels, setFilteredPanels] = useState([]);
    const [allPanels, setAllPanels] = useState([]);
    const [regions, setRegions] = useState([]);
    const [locations, setLocations] = useState([]);
    const [activeRegions, setActiveRegions] = useState([]);
    const [activeCities, setActiveCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadMore, setLoadMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [activePanel, setActivePanel] = useState(null);
    const [navOpen, setNavOpen] = useState(null);

    useEffect(() => {
        fetchData(false);

        setActiveRegions(getStoreRegions());
        setActiveCities(getStoreCities());

    }, []);

    useEffect(() => {
        if (!loading && getStorePanels().length > 0) {
            setPanels(getStorePanels());
            setHasMore(filteredPanels.length > getStorePanels().length);
        }
    }, [loading]);

    useEffect(() => {
        setStoreRegions(activeRegions);
    }, [activeRegions]);

    useEffect(() => {
        setStoreCities(activeCities);
    }, [activeCities]);

    useEffect(() => {
        if (!loading) {
            setStorePanels(panels);
        }
    }, [panels]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.keyCode === 27 && activePanel !== null) {
                setActivePanel(null);
            }
        };
        document.addEventListener("keydown", handleKeyDown, false);
        return () => {
            document.removeEventListener("keydown", handleKeyDown, false);
        };
    }, [activePanel]);

    useEffect(() => {
        if (loadMore && hasMore) {
            const currentLength = panels.length;
            const isMore = currentLength < filteredPanels.length;
            const nextResults = isMore
                ? filteredPanels.slice(currentLength, currentLength + pagesize)
                : [];

            setPanels([...panels, ...nextResults]);
            setLoadMore(false);
            setHasMore(
                currentLength + nextResults.length < filteredPanels.length
            );
        }
    }, [loadMore]);

    useEffect(() => {
        let newFilteredPanels = allPanels.filter((panel) => {
            let r = true;
            if (
                (activeRegions.length > 0 || activeCities.length > 0) &&
                !activeRegions.includes(panel.region) &&
                !activeCities.includes(panel.city)
            ) {
                r = false;
            }
            return r;
        });

        setFilteredPanels(newFilteredPanels);
        setPanels([...newFilteredPanels.slice(0, pagesize)]);
        setHasMore(pagesize < newFilteredPanels.length);

        if (locations) {
            locations.forEach((location) => {
                if (
                    location.cities.every((city) =>
                        activeCities.includes(city)
                    ) &&
                    !activeRegions.includes(location.region)
                ) {
                    setActiveRegions([...activeRegions, location.region]);
                }
            });
        }
    }, [activeRegions, activeCities, allPanels]);

    const fetchData = () => {
        const dom = document.getElementById("live-stream-page");
        const xhr = require("xhr");

        xhr.post(dom.dataset.link, (error, resp) => {
            const data = JSON.parse(resp.body);

            if (data.panels) {
                data.panels.sort((a, b) => {
                    return a.LiveStreamNumericSort - b.LiveStreamNumericSort;
                });
                setAllPanels(data.panels);

                const regionSortedPanels = [];
                regionSortedPanels.push(...data.panels);
                regionSortedPanels.sort((a, b) => {
                    return a.regionSort - b.regionSort;
                });

                let regs = [];
                let len = regionSortedPanels.length;
                for (let i = 0; i < len; i++) {
                    let r = regionSortedPanels[i].region;
                    if (regs.indexOf(r) === -1) {
                        regs.push(r);
                    }
                }
                handleLocations(regs, data.panels);
            }
            setLoading(false);
        });
    };

    const handleLoadMore = () => {
        setLoadMore(true);
    };

    const handleLocations = (regions, allPanels) => {
        const locationArray = [];
        regions.forEach((region) => {
            const children = [];
            allPanels.forEach((panel) => {
                if (panel.region === region && !children.includes(panel.city)) {
                    children.push(panel.city);
                }
            });
            if (children.length > 0) {
                locationArray.push({
                    region: region,
                    cities: children,
                    open: false,
                });
            }
        });
        setLocations(locationArray);
    };

    const toggleFilterOpen = (location) => {
        setLocations(
            locations.map((loc) =>
                loc.region === location.region
                    ? { ...loc, open: !loc.open }
                    : loc
            )
        );
    };

    const toggleNavOpen = () => {
        setNavOpen(!navOpen);
    };

    const handleRegionSelect = (location) => {
        if (activeRegions.includes(location.region)) {
            setActiveRegions(
                activeRegions.filter((region) => region !== location.region)
            );
            removeCitiesByRegion(location);
        } else {
            setActiveRegions([...activeRegions, location.region]);
            setActiveCities([
                ...new Set([...activeCities, ...location.cities]),
            ]);
        }
    };

    const handleCitySelect = (city, location) => {
        if (activeCities.includes(city)) {
            setActiveCities(activeCities.filter((c) => c !== city));
            setActiveRegions(
                activeRegions.filter((region) => region !== location.region)
            );
        } else {
            setActiveCities([...activeCities, city]);
        }
    };

    const handlePillSelect = (location) => {
        setActiveRegions(
            activeRegions.filter((region) => region !== location.region)
        );
        removeCitiesByRegion(location);
    };

    const removeCitiesByRegion = (location) => {
        const newCitiesArray = [];
        activeCities.forEach((city) => {
            if (!location.cities.includes(city)) {
                newCitiesArray.push(city);
            }
        });
        setActiveCities(newCitiesArray);
    };

    return (
        <>
            <div
                className="live-stream-nav-open-button"
                onClick={toggleNavOpen}
            >
                <div className="planner-filter-open-button__content">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 17.5 16.292"
                    >
                        <path
                            id="Path_15"
                            fill-rule="evenodd"
                            fill="#fff"
                            stroke="#fff"
                            stroke-width=".5"
                            d="M17.25 2.682h-3.1A3.036 3.036 0 0 0 11.175.25 3.036 3.036 0 0 0 8.2 2.682H.25v1.214h7.954a3.036 3.036 0 0 0 2.975 2.432 3.036 3.036 0 0 0 2.975-2.432h3.096Zm-6.071 2.429A1.822 1.822 0 0 1 9.358 3.29a1.822 1.822 0 0 1 1.821-1.821A1.822 1.822 0 0 1 13 3.29a1.822 1.822 0 0 1-1.821 1.821ZM.25 13.611h3.1a3.036 3.036 0 0 0 2.975 2.432A3.036 3.036 0 0 0 9.3 13.611h7.95v-1.214H9.296a3.036 3.036 0 0 0-2.975-2.432 3.036 3.036 0 0 0-2.975 2.432H.25Zm4.25-.607a1.821 1.821 0 0 1 1.821-1.821 1.821 1.821 0 0 1 1.821 1.821 1.821 1.821 0 0 1-1.821 1.821A1.821 1.821 0 0 1 4.5 13.003Z"
                            data-name="Path 15"
                        />
                    </svg>
                    <h4>{"Filters"}</h4>
                </div>
            </div>
            <div className="live-stream__wrapper">
                <div className="live-stream__inner">
                    <div
                        className={`live-stream__nav ${navOpen ? "open" : ""}`}
                    >
                        <div
                            className="live-stream-nav__close"
                            onClick={toggleNavOpen}
                        >
                            <CloseIconWhite />
                        </div>
                        <div className="live-stream__filter-icon">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 17.5 16.292"
                            >
                                <path
                                    id="Path_15"
                                    fill-rule="evenodd"
                                    stroke="#000"
                                    stroke-width=".5"
                                    d="M17.25 2.682h-3.1A3.036 3.036 0 0 0 11.175.25 3.036 3.036 0 0 0 8.2 2.682H.25v1.214h7.954a3.036 3.036 0 0 0 2.975 2.432 3.036 3.036 0 0 0 2.975-2.432h3.096Zm-6.071 2.429A1.822 1.822 0 0 1 9.358 3.29a1.822 1.822 0 0 1 1.821-1.821A1.822 1.822 0 0 1 13 3.29a1.822 1.822 0 0 1-1.821 1.821ZM.25 13.611h3.1a3.036 3.036 0 0 0 2.975 2.432A3.036 3.036 0 0 0 9.3 13.611h7.95v-1.214H9.296a3.036 3.036 0 0 0-2.975-2.432 3.036 3.036 0 0 0-2.975 2.432H.25Zm4.25-.607a1.821 1.821 0 0 1 1.821-1.821 1.821 1.821 0 0 1 1.821 1.821 1.821 1.821 0 0 1-1.821 1.821A1.821 1.821 0 0 1 4.5 13.003Z"
                                    data-name="Path 15"
                                />
                            </svg>
                            <h4>{"Filter"}</h4>
                        </div>
                        {activeCities.length > 0 &&
                            activeRegions.length > 0 && (
                                <>
                                    <div className="live-stream__filter-pills">
                                        {locations &&
                                            locations.map((location, i) => {
                                                let isCity = false;
                                                location.cities.forEach(
                                                    (city) => {
                                                        if (
                                                            activeCities.includes(
                                                                city
                                                            )
                                                        ) {
                                                            isCity = true;
                                                        }
                                                    }
                                                );
                                                if (
                                                    activeRegions.includes(
                                                        location.region
                                                    ) ||
                                                    isCity
                                                ) {
                                                    return (
                                                        <div
                                                            className="live-stream__filter-pill"
                                                            onClick={() => {
                                                                handlePillSelect(
                                                                    location
                                                                );
                                                            }}
                                                        >
                                                            <p>
                                                                {
                                                                    location.region
                                                                }
                                                            </p>
                                                            <CloseIcon />
                                                        </div>
                                                    );
                                                }

                                                return null;
                                            })}
                                    </div>
                                    <button
                                        onClick={() => {
                                            setActiveRegions([]);
                                            setActiveCities([]);
                                        }}
                                        className={"live-stream__filter-clear"}
                                    >
                                        Clear All
                                    </button>
                                </>
                            )}
                        <div className="live-stream__filter-subheading">
                            <p>Location</p>
                        </div>
                        {locations &&
                            locations.map((location, i) => {
                                return (
                                    <div className="live-stream__location">
                                        <div
                                            className={
                                                "live-stream__location_parent"
                                            }
                                            key={`location${i}`}
                                        >
                                            <button
                                                onClick={() => {
                                                    handleRegionSelect(
                                                        location
                                                    );
                                                }}
                                                className={
                                                    activeRegions.includes(
                                                        location.city
                                                    )
                                                        ? "active"
                                                        : ""
                                                }
                                            >
                                                <div className="filter-option-svg">
                                                    {getIcon(
                                                        true,
                                                        activeRegions.includes(
                                                            location.region
                                                        ) ||
                                                            activeCities.some(
                                                                (city) =>
                                                                    location.cities.includes(
                                                                        city
                                                                    )
                                                            ),
                                                        !activeRegions.includes(
                                                            location.region
                                                        ) &&
                                                            activeCities.some(
                                                                (city) =>
                                                                    location.cities.includes(
                                                                        city
                                                                    )
                                                            )
                                                    )}
                                                </div>
                                                {location.region}
                                            </button>
                                            <div
                                                className={`live-stream__toggle_icon ${
                                                    location.open
                                                        ? "active"
                                                        : ""
                                                }`}
                                                onClick={() => {
                                                    toggleFilterOpen(location);
                                                }}
                                            >
                                                <ToggleIcon />
                                            </div>
                                        </div>
                                        <div
                                            className={`live-stream__location_child ${
                                                location.open ? "open" : ""
                                            }`}
                                        >
                                            {location.cities.length > 0 &&
                                                location.cities.map((city) => {
                                                    return (
                                                        <button
                                                            onClick={() => {
                                                                handleCitySelect(
                                                                    city,
                                                                    location
                                                                );
                                                            }}
                                                        >
                                                            <div className="filter-option-svg">
                                                                {getIcon(
                                                                    true,
                                                                    activeCities.includes(
                                                                        city
                                                                    ),
                                                                    false
                                                                )}
                                                            </div>
                                                            {city}
                                                        </button>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                );
                            })}
                        <div className="live-stream-nav__mobile-select">
                            <p onClick={toggleNavOpen}>View items</p>
                        </div>
                    </div>
                    <div className="live-stream__content">
                        {loading && <p>Loading...</p>}

                        {activePanel && activePanel !== null && (
                            <div className="live-stream__popup">
                                <div className="live-stream__popup-inner">
                                    <button
                                        className="close"
                                        onClick={() => {
                                            setActivePanel(null);
                                        }}
                                        title="close"
                                    >
                                        <svg
                                            width="48"
                                            height="48"
                                            viewBox="0 0 48 48"
                                        >
                                            <path
                                                d="M31.6,15.4 L15.4,31.6 M15.4,15.4 L31.7,31.7"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                fill="none"
                                                fillRule="evenodd"
                                            />
                                        </svg>
                                    </button>

                                    <div className="video-wrapper">
                                        {activePanel.LiveStreamImprovingCamera && (
                                            <div class="video-wrapper__cameraImproving">
                                                An improved CCTV camera solution
                                                is in progress. If you require a
                                                link to what we have, please ask
                                                your sales representative.
                                            </div>
                                        )}
                                        <iframe
                                            width="560"
                                            height="315"
                                            src={`https://www.youtube.com/embed/${activePanel.youtubeLiveStreamID}?autoplay=1`}
                                            title="YouTube video player"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="live-stream__panels">
                            {panels &&
                                panels.map((panel) => {
                                    const disableCTA = panel.IsPublished === 0;
                                    const activeVideo =
                                        panel.youtubeLiveStreamID &&
                                        panel.LiveStreamDisplayVideo === 1;
                                    return (
                                        <div
                                            key={`panel${panel.id}`}
                                            className="live-stream__panel"
                                        >
                                            <div
                                                className={`live-stream__panel_inner${
                                                    activeVideo
                                                        ? " video-active"
                                                        : ""
                                                }`}
                                            >
                                                <button
                                                    className={
                                                        "live-stream__panel_image"
                                                    }
                                                    onClick={() => {
                                                        if (activeVideo) {
                                                            setActivePanel(
                                                                panel
                                                            );
                                                        }
                                                    }}
                                                    style={{
                                                        backgroundImage: `url("${panel.image}")`,
                                                    }}
                                                >
                                                    {activeVideo ? (
                                                        <svg
                                                            x="0px"
                                                            y="0px"
                                                            viewBox="0 0 60 60"
                                                            width="60"
                                                            height="60"
                                                        >
                                                            <path d="M30,0C13.43,0,0,13.43,0,30s13.43,30,30,30s30-13.43,30-30S46.57,0,30,0z M23.74,42.69l-0.13-25.73l18.4,12.83L23.74,42.69z" />
                                                            <polygon points="23.74,42.69 23.61,16.96 42.02,29.79 " />
                                                        </svg>
                                                    ) : (
                                                        <div className="live-stream__panel--disabled">
                                                            <div>
                                                                Live stream
                                                                coming soon.
                                                            </div>
                                                        </div>
                                                    )}
                                                </button>
                                                <div className="live-stream__panel-text">
                                                    <h5>{panel.name}</h5>
                                                    <div className="live-stream__panel-text__body">
                                                        <p>{panel.city}</p>
                                                        <p className="live-stream__panel-text__divider">
                                                            <span>{" | "}</span>
                                                        </p>
                                                        <p>{panel.siteID}</p>
                                                    </div>
                                                    <a
                                                        href={panel.link}
                                                        className="button"
                                                        disabled={disableCTA}
                                                        onClick={(e) => {
                                                            if (disableCTA) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    >
                                                        Find out more
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>

                        {hasMore && (
                            <p className="loadmore">
                                <button
                                    onClick={handleLoadMore}
                                    className="button light"
                                >
                                    Load more
                                </button>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default App;
